import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { FileText, Download, Send, Calendar, Filter, BarChart3 } from "lucide-react";

const REPORT_TYPES = [
  { value: "sales_summary", label: "Sales Summary" },
  { value: "pipeline_analysis", label: "Pipeline Analysis" },
  { value: "activity_report", label: "Activity Report" },
  { value: "forecast_report", label: "Forecast Report" },
  { value: "custom", label: "Custom Report" },
];

const CHART_TYPES = [
  { value: "bar", label: "Bar Chart" },
  { value: "line", label: "Line Chart" },
  { value: "pie", label: "Pie Chart" },
  { value: "table", label: "Data Table" },
];

const DATE_RANGES = [
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "last_quarter", label: "Last Quarter" },
  { value: "last_year", label: "Last Year" },
  { value: "custom", label: "Custom Range" },
];

const automatedReports = [
  {
    name: "Daily Pipeline Update",
    frequency: "Daily",
    lastSent: "2024-01-29",
    recipients: 3,
    status: "active",
  },
  {
    name: "Weekly Performance Summary",
    frequency: "Weekly",
    lastSent: "2024-01-26",
    recipients: 5,
    status: "active",
  },
  {
    name: "Monthly Revenue Report",
    frequency: "Monthly",
    lastSent: "2024-01-01",
    recipients: 8,
    status: "active",
  },
];

export const ReportBuilder: React.FC = () => {
  const [reportConfig, setReportConfig] = useState({
    name: "",
    type: "",
    dateRange: "",
    chartType: "",
    filters: [] as string[],
  });

  const handleAddFilter = (filter: string) => {
    setReportConfig(prev => ({
      ...prev,
      filters: [...prev.filters, filter]
    }));
  };

  const handleRemoveFilter = (index: number) => {
    setReportConfig(prev => ({
      ...prev,
      filters: prev.filters.filter((_, i) => i !== index)
    }));
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Report Builder */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Custom Report Builder
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="reportName">Report Name</Label>
              <Input
                id="reportName"
                value={reportConfig.name}
                onChange={(e) => setReportConfig(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter report name"
              />
            </div>

            <div>
              <Label htmlFor="reportType">Report Type</Label>
              <Select value={reportConfig.type} onValueChange={(value) => setReportConfig(prev => ({ ...prev, type: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select report type" />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="dateRange">Date Range</Label>
              <Select value={reportConfig.dateRange} onValueChange={(value) => setReportConfig(prev => ({ ...prev, dateRange: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select date range" />
                </SelectTrigger>
                <SelectContent>
                  {DATE_RANGES.map((range) => (
                    <SelectItem key={range.value} value={range.value}>
                      {range.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="chartType">Visualization Type</Label>
              <Select value={reportConfig.chartType} onValueChange={(value) => setReportConfig(prev => ({ ...prev, chartType: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Select chart type" />
                </SelectTrigger>
                <SelectContent>
                  {CHART_TYPES.map((chart) => (
                    <SelectItem key={chart.value} value={chart.value}>
                      {chart.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Filters</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {reportConfig.filters.map((filter, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => handleRemoveFilter(index)}
                  >
                    {filter} ×
                  </Badge>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddFilter("Sample Filter")}
                className="w-full"
              >
                <Filter className="h-4 w-4 mr-2" />
                Add Filter
              </Button>
            </div>

            <div className="flex gap-2">
              <Button className="flex-1">
                <FileText className="h-4 w-4 mr-2" />
                Preview Report
              </Button>
              <Button variant="outline" className="flex-1">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Automated Reports */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Automated Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {automatedReports.map((report, index) => (
                <div key={index} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-sm">{report.name}</h4>
                    <Badge variant={report.status === "active" ? "default" : "secondary"}>
                      {report.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>
                      <p>Frequency: {report.frequency}</p>
                      <p>Recipients: {report.recipients}</p>
                    </div>
                    <div>
                      <p>Last Sent: {report.lastSent}</p>
                    </div>
                  </div>
                  <div className="flex gap-1 mt-3">
                    <Button variant="outline" size="sm" className="text-xs">
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs">
                      Send Now
                    </Button>
                  </div>
                </div>
              ))}
              <Button variant="outline" className="w-full">
                <Calendar className="h-4 w-4 mr-2" />
                Schedule New Report
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Report Templates */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Report Templates</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {REPORT_TYPES.slice(0, 4).map((template) => (
              <Button key={template.value} variant="outline" className="h-20 flex-col">
                <FileText className="h-6 w-6 mb-2" />
                <span className="text-sm">{template.label}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};